import {ActiveFormAsset, defaultAssetsState, InEstateAssetTotals} from "../../../Assets/clientAssetsSlice";
import {AssetsSummary} from "../../../Assets/models/Assets";
import {InvestorGroupType, MemberGroup} from "../../../ClientManagement/models/InvestorGroupType";
import {StandaloneAccount} from "../../../Assets/models/StandaloneAccount";
import {PersonalAsset} from "../../../Assets/models/PersonalAsset";
import {GeneralInflow} from "../../../Assets/models/GeneralInflow";
import {SocialSecurity} from "../../../Assets/models/SocialSecurity";
import {InvestmentProgram, LegalAgreement} from "../../../Assets/models/InvestmentProgram";
import {PersonalLiabilitySummary} from "../../../Assets/models/PersonalLiability";
import {LifeInsurance} from "../../../Assets/models/LifeInsurance";
import {
    assetListData,
    getAllAccounts,
    hasAssets,
    hasInEstateAssets,
    hasLiabilities,
    hasOutOfEstateAssets,
    hasOutOfEstateLiabilities,
    sortByOrdinalAndAfterTaxVestedValue,
    sortByOrdinalAndCashValue,
    sortByOrdinalAndLoanBalance,
    sortByOrdinalAndPresentValue
} from "../../../Assets/AssetSummary/common/utils";
import React, {useEffect, useMemo, useState} from "react";
import {formatCurrency} from "../../../utils/format";
import {
    calculateInEstateTotalValue,
    calculateOutOfEstateTotalValue,
} from "../../../Assets/AssetSummary/common/AssetSummaryCalculator";
import PrintViewWrapper from "../PrintViewWrapper";
import {BarChartSidebar} from "../../../components";
import {createAssetBarChartData} from "../../../Assets/AssetSummary/BarChart/AssetBarChartUtils";
import {AssetBarChartFooter} from "../../../Assets/AssetSummary/BarChart/AssetBarChartFooter";
import {AssetReportList} from "./AssetReportList";
import {LiabilityReportList} from "./LiabilityReportList";
import {LegalEntityReadModel} from "../../../Assets/models/Ownership";
import {
    createLegalEntityTypeSummariesWithBankingDetails,
    createNonClientMemberSummaries,
    LegalEntityTypeSummary,
    NonClientMemberSummary
} from "../../../Assets/mappers";
import OutOfEstateLegalEntityReportView from "./OutOfEstateLegalEntityReportView";
import OutOfEstateNonClientSummariesView from "./OutOfEstateNonClientSummariesView";
import {setAccordionPreferencesState} from "../../../Assets/common/accordionPreferencesSlice";
import {useAppDispatch} from "../../../store/hooks";
import {
    hasOutOfEstateLegalEntities,
    hasOutOfEstateNonClientSummaries,
    InAndOutEstateAssets,
    PAGE_CONSTANTS,
    splitNonClientMemberSummaries,
    splitOutOfEstateLegalEntities
} from "./AssetsReportUtils";
import {EquityCompensationFormData} from "../../../Assets/models/EquityCompensation";
import {StandaloneAccountOrLegalAgreement} from "../../../Assets/AssetSummary/InEstate/AccountTableSection";
import {PartiallyOwnedLegalAgreement} from "../../../Assets/models/PartiallyOwnedInvestmentAccount";
import {
    calculateVisibleOutOfEstateReportTotalValue,
    filterAssetsForOutOfEstateReport,
    filterEmptyLegalEntityTypeSummaries
} from "../../../Assets/common/OutOfEstate/outOfEstateReportVisibility";


export type AssetSummaryReportViewProps = {
    inEstateAssetsTotals: InEstateAssetTotals,
    activeFormAsset: ActiveFormAsset,
    displayName: string,
    clientAssets: AssetsSummary,
    investorGroup: InvestorGroupType,
    profileId: string,
    memberGroup?: MemberGroup,
    legalEntities: LegalEntityReadModel[],
    isDetailed: boolean
}

type AssetTypeList =
    StandaloneAccount[]
    | PersonalAsset[]
    | GeneralInflow[]
    | SocialSecurity[]
    | LegalAgreement[]
    | PersonalLiabilitySummary[]
    | LifeInsurance[]
    | EquityCompensationFormData[]
    | StandaloneAccountOrLegalAgreement[]

export function OutOfEstateReportView(props: { assets: AssetsSummary, allItems: any[], clientAssetsPage: InAndOutEstateAssets, allLegalEntityTypeSummaries: LegalEntityTypeSummary[], detailed: boolean, outOfEstateAccordionReport: string, outOfEstateCollapsedReport: string, allNonClientMemberSummaries: NonClientMemberSummary[], outOfEstateNonClientAccordionReport: string, outOfEstateNonClientCollapsedReport: string }) {
    return <>
        {
            <>
                <OutOfEstateLegalEntityReportView
                    allItems={props.allItems}
                    legalEntitiesSummariesForPage={props.clientAssetsPage.legalEntitySummary!}
                    allLegalEntityTypeSummaries={props.allLegalEntityTypeSummaries}
                    accordionIdForOutOfEstate={props.detailed ? props.outOfEstateAccordionReport : props.outOfEstateCollapsedReport}
                    isDetailed={props.detailed}
                />
            </>
        }
        {
            hasOutOfEstateNonClientSummaries(props.clientAssetsPage) &&
            <OutOfEstateNonClientSummariesView
                allItems={props.allItems}
                nonClientMemberSummariesForPage={props.clientAssetsPage.nonClientMemberSummary!}
                allNonClientMemberSummaries={props.allNonClientMemberSummaries}
                accordionIdForView={props.detailed ? props.outOfEstateNonClientAccordionReport : props.outOfEstateNonClientCollapsedReport}
            />
        }
    </>;
}

export function AssetSummaryReportView({
                                           investorGroup,
                                           profileId,
                                           inEstateAssetsTotals,
                                           activeFormAsset,
                                           displayName,
                                           clientAssets,
                                           memberGroup,
                                           legalEntities,
                                           isDetailed
                                       }: AssetSummaryReportViewProps) {

    const dispatch = useAppDispatch();
    const [assetSummaryReportPages, setAssetSummaryReportPages] = useState<InAndOutEstateAssets[]>([]);

    const hasAccounts = (clientAssetsData: AssetsSummary) => {
        if (!clientAssets) return false;
        return clientAssetsData.accounts?.data?.length > 0;
    }

    const hasInvestmentPrograms = (clientAssetsData: AssetsSummary) => {
        if (!clientAssets) return false;
        return clientAssetsData.investmentProgram && clientAssetsData.investmentProgram.legalAgreements.length > 0
    }

    const hasHeldAwayAccounts = (clientAssetsData: AssetsSummary) => {
        if (!clientAssets) return false;
        return clientAssetsData.investmentProgram && clientAssetsData.investmentProgram.heldAwayAccounts.length > 0
    }

    const hasBankingAccounts = (clientAssetsData: AssetsSummary) => {
        if (!clientAssets) return false;
        return clientAssetsData.bankingAccounts && clientAssetsData.bankingAccounts.length > 0
    }

    const hasPartiallyOwnedLegalAgreements = (clientAssetsData: AssetsSummary) => {
        if (!clientAssets) return false;
        return (clientAssetsData) && (clientAssetsData).partiallyOwnedLegalAgreements.length > 0
    }
    const hasGeneralInflows = (clientAssetsData: AssetsSummary) => {
        if (!clientAssets) return false;
        return clientAssetsData.generalInflows?.data?.length > 0;
    }

    const hasSocialSecurity = (clientAssetsData: AssetsSummary) => {
        if (!clientAssets) return false;
        return clientAssetsData.socialSecurities?.data?.length > 0;
    }

    const populateAssetsList = (assetType: keyof AssetsSummary, data: AssetTypeList): Partial<AssetsSummary> => {
        switch (assetType) {
            case 'investmentProgram':
                return {
                    investmentProgram: {
                        ...clientAssets.investmentProgram,
                        legalAgreements: data as LegalAgreement[]
                    } as InvestmentProgram
                }
            case 'partiallyOwnedLegalAgreements':
                return {
                    partiallyOwnedLegalAgreements: data as PartiallyOwnedLegalAgreement[]
                }
            case 'personalLiabilities':
                return {
                    personalLiabilities: data as PersonalLiabilitySummary[]
                }
            case 'equityCompensations':
                return {
                    equityCompensations: {
                        ...clientAssets.equityCompensations,
                        data:data as EquityCompensationFormData[]
                    }
                }
            default:
                const assetTypeData = clientAssets[assetType];
                if (typeof assetTypeData === 'object' && typeof assetTypeData != 'number') {
                    return {
                        [assetType]: {
                            ...assetTypeData,
                            data
                        }
                    }
                } else {
                    return {}
                }
        }
    };

    function getTotalRows(assetType: string, data: AssetTypeList) {
        let totalRows = 0;
        if (assetType === 'equityCompensations') {
            totalRows = data.length;

            data.forEach(elem => {
                totalRows += 1;
                if("stockOptionsAsOfDate" in elem && elem.stockOptionsAsOfDate != null){
                    totalRows += 1;
                }
                if ("stockGrantsAsOfDate" in elem && elem.stockGrantsAsOfDate != null) {
                    totalRows += 1;
                }
            })
        } else {
            return data.length;
        }
        return totalRows;
    }

    function getCountOfPagesThatCanFit(assetType: string ,data:AssetTypeList, remainingPageSpace: number) {
        if (assetType === 'equityCompensations') {
            let tempSpace = 0, i = 0;
            for (i; i < data.length; i++) {
                const elem = data[i];
                if (tempSpace > remainingPageSpace) break;
                tempSpace += PAGE_CONSTANTS.TABLE_ROW_HEIGHT;
                if ("stockOptionsAsOfDate" in elem && elem.stockOptionsAsOfDate != null) {
                    tempSpace += PAGE_CONSTANTS.TABLE_ROW_HEIGHT;
                }
                if ("stockGrantsAsOfDate" in elem && elem.stockGrantsAsOfDate != null) {
                    tempSpace += PAGE_CONSTANTS.TABLE_ROW_HEIGHT;
                }
            }
            return i;
        } else {
            return Math.floor(remainingPageSpace / PAGE_CONSTANTS.TABLE_ROW_HEIGHT);
        }
    }

    const setPageAssetData = (assetType: keyof AssetsSummary, pageAssets: AssetsSummary, pageScore: number, data: AssetTypeList, calculatedAssetsPages: AssetsSummary[], isNestedAssetType: boolean) => {
        const nestedAssetsHeaderLength = isNestedAssetType ? 1 : 0;
        let totalRows = getTotalRows(assetType, data);

        let updatedPageScore = pageScore + (totalRows + nestedAssetsHeaderLength) * PAGE_CONSTANTS.TABLE_ROW_HEIGHT;
        if (updatedPageScore < PAGE_CONSTANTS.TOTAL_PAGE_HEIGHT) {
            pageAssets = {
                ...pageAssets,
                ...populateAssetsList(assetType, data)
            }
        } else {
            const remainingPageSpace = PAGE_CONSTANTS.TOTAL_PAGE_HEIGHT - pageScore;
            let noOfAssetsPageCanFit = 0;
            if (remainingPageSpace > 0) {
                // If there is enough space to fit 3 assets, slice the 3 and push them to current page
                noOfAssetsPageCanFit = getCountOfPagesThatCanFit(assetType, data, remainingPageSpace);
                if (noOfAssetsPageCanFit > 0) {
                    const assetsThatCanFitInCurrentPage = data.slice(0, noOfAssetsPageCanFit);
                    pageAssets = {
                        ...pageAssets,
                        ...populateAssetsList(assetType, assetsThatCanFitInCurrentPage)
                    };
                }
            }
            // Push Current Page
            calculatedAssetsPages.push(pageAssets);

            // Creating New Page
            updatedPageScore = PAGE_CONSTANTS.PAGE_PADDING_HEIGHT + PAGE_CONSTANTS.SECTION_HEADER_HEIGHT + PAGE_CONSTANTS.ASSET_LIABILITY_BOTTOM_MARGIN;
            pageAssets = defaultAssetsState;

            // For rest of the assets, split the remaining data recursively until all assets are fit into 1 or more pages
            const remainingAssets = data.slice(noOfAssetsPageCanFit);

            if (remainingAssets.length > 0) {

                const result = setPageAssetData(assetType, pageAssets, updatedPageScore, remainingAssets, calculatedAssetsPages, isNestedAssetType);
                updatedPageScore = result.pageScore;
                pageAssets = result.pageAssets;
            }
        }

        return {pageScore: updatedPageScore, pageAssets};
    }

    const getEstimatesForInvestmentsAndAccounts = (assetsData: AssetsSummary, pageScore: number, pageAssets: AssetsSummary, calculatedAssets: AssetsSummary[]) => {
        if (hasAccounts(assetsData) || hasInvestmentPrograms(assetsData) || hasHeldAwayAccounts(assetsData) || hasBankingAccounts(assetsData) || hasPartiallyOwnedLegalAgreements(assetsData)) {
            pageScore = pageScore + PAGE_CONSTANTS.ACCORDION_HEADER_HEIGHT + PAGE_CONSTANTS.TABLE_HEADER_HEIGHT;

            const allAccounts = getAllAccounts(assetsData);

            const result = setPageAssetData('accounts', pageAssets, pageScore, allAccounts, calculatedAssets, false);
            pageScore = result.pageScore;
            pageAssets = result.pageAssets;
        }
        return {pageScore, pageAssets};
    }

    const getEstimatesForAssets = (assetsData: AssetsSummary, pageScore: number, pageAssets: AssetsSummary, calculatedAssets: AssetsSummary[]) => {
        if (hasAssets(assetsData)) {
            const __ret = getEstimatesForInvestmentsAndAccounts(assetsData, pageScore, pageAssets, calculatedAssets);
            pageScore = __ret.pageScore;
            pageAssets = __ret.pageAssets;

            if (assetsData.equityCompensations.data.length > 0) {
                pageScore = pageScore + PAGE_CONSTANTS.ACCORDION_HEADER_HEIGHT + PAGE_CONSTANTS.TABLE_HEADER_HEIGHT;
                const result = setPageAssetData('equityCompensations', pageAssets, pageScore, sortByOrdinalAndAfterTaxVestedValue(assetsData.equityCompensations.data) as EquityCompensationFormData[], calculatedAssets, true);
                pageScore = result.pageScore;
                pageAssets = result.pageAssets;
            }

            if (hasGeneralInflows(assetsData) || hasSocialSecurity(assetsData)) {
                pageScore = pageScore + PAGE_CONSTANTS.ACCORDION_HEADER_HEIGHT + PAGE_CONSTANTS.TABLE_HEADER_HEIGHT;
                if (hasGeneralInflows(assetsData)) {
                    const result = setPageAssetData('generalInflows', pageAssets, pageScore, sortByOrdinalAndPresentValue(assetsData.generalInflows.data) as GeneralInflow[] , calculatedAssets, false);
                    pageScore = result.pageScore;
                    pageAssets = result.pageAssets;
                }

                if (hasSocialSecurity(assetsData)) {
                    const result = setPageAssetData('socialSecurities', pageAssets, pageScore, sortByOrdinalAndPresentValue(assetsData.socialSecurities.data) as SocialSecurity[], calculatedAssets, false);
                    pageScore = result.pageScore;
                    pageAssets = result.pageAssets;
                }
            }

            if (assetsData.personalAssets.data.length > 0) {
                pageScore = pageScore + PAGE_CONSTANTS.ACCORDION_HEADER_HEIGHT + PAGE_CONSTANTS.TABLE_HEADER_HEIGHT;
                const result = setPageAssetData('personalAssets', pageAssets, pageScore, sortByOrdinalAndPresentValue(assetsData.personalAssets.data) as PersonalAsset[], calculatedAssets, false);
                pageScore = result.pageScore;
                pageAssets = result.pageAssets;
            }
            if (assetsData.lifeInsurances.data.length > 0) {
                pageScore = pageScore + PAGE_CONSTANTS.ACCORDION_HEADER_HEIGHT + PAGE_CONSTANTS.TABLE_HEADER_HEIGHT;
                const result = setPageAssetData('lifeInsurances', pageAssets, pageScore, sortByOrdinalAndCashValue(assetsData.lifeInsurances.data) as  LifeInsurance[], calculatedAssets, false);
                pageScore = result.pageScore;
                pageAssets = result.pageAssets;
            }

        }
        return {pageScore, pageAssets};
    }

    const splitAssets = (assetsData: AssetsSummary) => {
        const calculatedAssets: AssetsSummary[] = [];
        let pageScore = PAGE_CONSTANTS.PAGE_PADDING_HEIGHT + PAGE_CONSTANTS.SECTION_HEADER_HEIGHT;
        let pageAssets: AssetsSummary = {...defaultAssetsState};

        const __ret = getEstimatesForAssets(assetsData, pageScore, pageAssets, calculatedAssets);
        pageScore = __ret.pageScore;
        pageAssets = __ret.pageAssets;

        if (hasLiabilities(assetsData)) {
            if (hasAssets(assetsData)) {
                pageScore += PAGE_CONSTANTS.ASSET_LIABILITY_BOTTOM_MARGIN;
            }
            pageScore += PAGE_CONSTANTS.ASSET_LIABILITY_BOTTOM_MARGIN;

            pageScore += PAGE_CONSTANTS.ACCORDION_HEADER_HEIGHT + PAGE_CONSTANTS.TABLE_HEADER_HEIGHT;

            const result = setPageAssetData('personalLiabilities', pageAssets, pageScore, sortByOrdinalAndLoanBalance(assetsData.personalLiabilities) as PersonalLiabilitySummary[] , calculatedAssets, false);
            pageAssets = result.pageAssets;
            pageScore = result.pageScore;
        }

        if (pageAssets !== defaultAssetsState) {
            calculatedAssets.push(pageAssets);
        }

        let spaceLeft: number = 0;
        if (PAGE_CONSTANTS.TOTAL_PAGE_HEIGHT < pageScore) spaceLeft = 2 * PAGE_CONSTANTS.TOTAL_PAGE_HEIGHT - pageScore;
        else spaceLeft = PAGE_CONSTANTS.TOTAL_PAGE_HEIGHT - pageScore;
        return {assetSummariesData: calculatedAssets, remainingSpaceAfterSplittingAssets: spaceLeft};
    }

    const [firstLegalEntityPageIndex, setFirstLegalEntityPageIndex] = useState<number>();
    const [legalEntityTypeSummaries, setLegalEntityTypeSummaries] = useState<LegalEntityTypeSummary[]>([]);
    const [nonClientMemberSummaries, setNonClientMemberSummaries] = useState<NonClientMemberSummary[]>([]);
    const [allItems, setAllItems] = useState<any[]>([]);
    const reportVisibleOutOfEstateAssets = useMemo(
        () => filterAssetsForOutOfEstateReport(clientAssets),
        [clientAssets]
    );
    const outOfEstateSectionVisible = hasOutOfEstateReportContent(clientAssets, assetSummaryReportPages[firstLegalEntityPageIndex ?? 0]);

    function expandNonClientAccordions(nonClientNamesToExpand: string[]) {
        const nonClientAccordionNames = nonClientNamesToExpand.map((accordionName) => accordionName + "-report");
        dispatch(setAccordionPreferencesState({
            accordionId: "AssetSummaryOutOfEstateNonClientAccordionReport",
            state: {
                expandedItems: [...nonClientAccordionNames]
            }
        }));
    }

    useEffect(() => {
        const assetSummaryPagesState = isDetailed
            ? splitAssets(clientAssets)
            : {
                assetSummariesData: [clientAssets],
                remainingSpaceAfterSplittingAssets: PAGE_CONSTANTS.TOTAL_PAGE_HEIGHT
            };

        const reportPagesState = getAssetSummaryReportPages(
            assetSummaryPagesState.assetSummariesData.map(createAssetsSummaryPage),
            reportVisibleOutOfEstateAssets,
            legalEntities,
            assetSummaryPagesState.remainingSpaceAfterSplittingAssets
        );

        setFirstLegalEntityPageIndex(reportPagesState.firstLegalEntityPageIndex);
        setAllItems(reportPagesState.allItems);
        setLegalEntityTypeSummaries(reportPagesState.legalEntityTypeSummaries);
        setNonClientMemberSummaries(reportPagesState.nonClientMemberSummaries);

        if (!isDetailed || assetSummaryReportPages.length === 0) {
            setAssetSummaryReportPages(reportPagesState.pages);

            if (isDetailed) {
                expandNonClientAccordions(reportPagesState.nonClientNames);
            }
        }
    }, [clientAssets]);

    const getInestateTotalValue = (pageIndex: number) => {
        return (pageIndex === 0) ? <div>You
            have <b>{formatCurrency(calculateInEstateTotalValue(clientAssets))}</b> in
            assets:
        </div> : ""
    }

    let OutOfEstateAccordionReport = "AssetSummaryOutOfEstateAccordionReport";
    let OutOfEstateCollapsedReport = "AssetSummaryOutOfEstateCollapsedReport";
    let OutOfEstateNonClientAccordionReport = "AssetSummaryOutOfEstateNonClientAccordionReport";
    let OutOfEstateNonClientCollapsedReport = "AssetSummaryOutOfEstateNonClientCollapsedReport";
    return (
        <>
            {assetSummaryReportPages.map((clientAssetsPage, pageIndex) => (
                <PrintViewWrapper pageNumber={pageIndex} displayName={displayName} key={pageIndex}>
                    <article className="asset-summary-page layout-split-left">
                        <BarChartSidebar
                            data={createAssetBarChartData(inEstateAssetsTotals, activeFormAsset)}
                            noDataText='No assets have been captured yet.'
                            displayName={displayName}
                            title={`Your Assets${pageIndex > 0 ? ' (continued)' :''}`}>
                            <AssetBarChartFooter
                                clientAssets={clientAssets}
                                inEstateAssetsTotals={inEstateAssetsTotals}
                                activeFormAsset={activeFormAsset}
                                investorGroup={investorGroup}
                                title='Total Net Assets'
                            />
                        </BarChartSidebar>
                        <section>
                            <div data-testid="asset-summary-content">
                                {clientAssetsPage && <div className="asset-summary-content">
                                    {clientAssetsPage.assetsSummary && hasAssets(clientAssetsPage.assetsSummary) &&
                                        <>
                                            <div className="section-header">
                                                <h3>
                                                    {getInestateTotalValue(pageIndex)}
                                                    {!hasInEstateAssets(clientAssetsPage.assetsSummary) &&
                                                        <div className="condensed-subtitle">Begin by entering assets or
                                                            entities</div>}
                                                </h3>
                                            </div>
                                            <AssetReportList
                                                assetsDataForPage={clientAssetsPage.assetsSummary}
                                                investorGroup={investorGroup}
                                                profileId={profileId}
                                                memberGroup={memberGroup}
                                                allAssets={clientAssets}
                                                isDetailed={isDetailed}
                                            />
                                        </>
                                    }
                                    {clientAssetsPage.assetsSummary && assetListData(clientAssetsPage.assetsSummary).hasInEstatePersonalLiability &&
                                        <>
                                            {(clientAssets.personalLiabilities.findIndex(liab => liab.id === clientAssetsPage.assetsSummary!.personalLiabilities[0].id)) === 0 &&
                                                <h3>
                                                    You
                                                    have <b>{formatCurrency(clientAssets.liabilitiesValue.inEstateValue)}</b> in
                                                    personal
                                                    liabilities:
                                                </h3>
                                            }
                                            {hasLiabilities(clientAssets) &&
                                                <LiabilityReportList
                                                    allAssets={clientAssets}
                                                    clientAssets={clientAssetsPage.assetsSummary!}
                                                    isDetailed={isDetailed}
                                                />
                                            }
                                        </>
                                    }
                                    {
                                        pageIndex === firstLegalEntityPageIndex && outOfEstateSectionVisible &&
                                        <div className="section-header">
                                            <h3>
                                                Out of estate assets
                                                total <b>{formatCurrency(calculateVisibleOutOfEstateReportTotalValue(clientAssets)).trim()}</b>:
                                            </h3>
                                        </div>
                                    }
                                    {
                                        hasOutOfEstateReportContent(clientAssets, clientAssetsPage) &&
                                        (clientAssetsPage.legalEntitySummary || clientAssetsPage.nonClientMemberSummary) &&
                                        <OutOfEstateReportView
                                            assets={clientAssets} allItems={allItems}
                                            clientAssetsPage={clientAssetsPage}
                                            allLegalEntityTypeSummaries={legalEntityTypeSummaries}
                                            detailed={isDetailed}
                                            outOfEstateAccordionReport={OutOfEstateAccordionReport}
                                            outOfEstateCollapsedReport={OutOfEstateCollapsedReport}
                                            allNonClientMemberSummaries={nonClientMemberSummaries}
                                            outOfEstateNonClientAccordionReport={OutOfEstateNonClientAccordionReport}
                                            outOfEstateNonClientCollapsedReport={OutOfEstateNonClientCollapsedReport}/>
                                    }
                                </div>
                                }
                            </div>
                        </section>
                    </article>
                </PrintViewWrapper>
            ))}
        </>
    )
}

const createReportPage = (page: Partial<InAndOutEstateAssets>): InAndOutEstateAssets => ({
    assetsSummary: undefined,
    legalEntitySummary: undefined,
    nonClientMemberSummary: undefined,
    ...page
});

const getOutOfEstateReportData = (
    reportVisibleOutOfEstateAssets: AssetsSummary,
    legalEntities: LegalEntityReadModel[],
    remainingSpaceAfterSplittingAssets: number
) => {
    const tempLegalEntityTypeSummaries = filterEmptyLegalEntityTypeSummaries(
        createLegalEntityTypeSummariesWithBankingDetails('AssetSummary', reportVisibleOutOfEstateAssets, legalEntities)
    );
    const {
        legalEntityTypeSummariesArray,
        remainingSpaceAfterLegalEntities
    } = splitOutOfEstateLegalEntities(tempLegalEntityTypeSummaries, remainingSpaceAfterSplittingAssets);

    const legalEntitySummariesPages = legalEntityTypeSummariesArray.map((summary) => createReportPage({
        legalEntitySummary: summary
    }));

    const tempNonClientMemberSummaries = createNonClientMemberSummaries('AssetSummary', reportVisibleOutOfEstateAssets);
    const nonClientMemberSummariesArray: Array<NonClientMemberSummary[]> = splitNonClientMemberSummaries(tempNonClientMemberSummaries, remainingSpaceAfterLegalEntities);
    const nonClientMemberSummariesPages = nonClientMemberSummariesArray.map((summary) => createReportPage({
        nonClientMemberSummary: summary
    }));

    const legalEntityNames = [...tempLegalEntityTypeSummaries.map((summary) => summary.entityType)];
    const nonClientNames = [
        ...tempNonClientMemberSummaries.map((summary) => `${summary.memberName}-assets-summary`)
    ];

    return {
        legalEntitySummariesPages,
        nonClientMemberSummariesPages,
        legalEntityNames,
        nonClientNames,
        tempLegalEntityTypeSummaries,
        tempNonClientMemberSummaries,
    };
};

const createAssetsSummaryPage = (assetsSummary: AssetsSummary): InAndOutEstateAssets => createReportPage({
    assetsSummary
});

const getAssetSummaryReportPages = (
    assetSummariesPages: InAndOutEstateAssets[],
    reportVisibleOutOfEstateAssets: AssetsSummary,
    legalEntities: LegalEntityReadModel[],
    remainingSpaceAfterSplittingAssets: number
) => {
    const {
        legalEntitySummariesPages,
        nonClientMemberSummariesPages,
        legalEntityNames,
        nonClientNames,
        tempLegalEntityTypeSummaries,
        tempNonClientMemberSummaries,
    } = getOutOfEstateReportData(reportVisibleOutOfEstateAssets, legalEntities, remainingSpaceAfterSplittingAssets);

    return {
        pages: [...assetSummariesPages, ...legalEntitySummariesPages, ...nonClientMemberSummariesPages],
        firstLegalEntityPageIndex: assetSummariesPages.length,
        allItems: [...legalEntityNames, ...nonClientNames],
        legalEntityTypeSummaries: tempLegalEntityTypeSummaries,
        nonClientMemberSummaries: tempNonClientMemberSummaries,
        nonClientNames
    };
};

const hasOutOfEstateReportContent = (
    clientAssets: AssetsSummary,
    clientAssetsPage?: InAndOutEstateAssets
) => {
    return hasOutOfEstateAssets(clientAssets)
        || hasOutOfEstateLiabilities(clientAssets)
        || (clientAssetsPage ? hasOutOfEstateLegalEntities(clientAssetsPage) : false)
        || (clientAssetsPage ? hasOutOfEstateNonClientSummaries(clientAssetsPage) : false);
};

